import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getLicenses, createLicense, deactivateLicense, deleteLicense, getMasterKeyQuotas, createMasterKey, renewLicense } from '../services/api';
import { getAdminInfo } from '../services/auth';
import { canRenewLicense, calculateDefaultDurationDays, toDateTimeLocalValue } from '../utils/licenseRenewal';
import './LicenseManagement.css';

function LicenseManagement() {
  console.log('LicenseManagement COMPONENT RENDERED');
  const { t } = useTranslation();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filters, setFilters] = useState({
    product_code: '',
    license_type: '',
    status: '',
  });

  const [newLicense, setNewLicense] = useState({
    product_code: 'ZEITECE',
    license_type: 'TRIAL',
    expires_at: '',
  });

  const [quotas, setQuotas] = useState([]);
  const [masterKeyInput, setMasterKeyInput] = useState('');
  const [masterKeySubmitting, setMasterKeySubmitting] = useState(false);
  const [masterKeyError, setMasterKeyError] = useState('');

  const [showRenewModal, setShowRenewModal] = useState(false);
  const [renewTargetLicense, setRenewTargetLicense] = useState(null);
  const [renewSubmitting, setRenewSubmitting] = useState(false);
  const [renewForm, setRenewForm] = useState({
    extend_days: 365,
    expires_at: '',
    mode: 'days',
  });

  const adminInfo = getAdminInfo();
  const renewableLicenses = useMemo(() => licenses.filter(canRenewLicense), [licenses]);

  const loadLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== '')
      );
      const data = await getLicenses(activeFilters);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/9f295211-a98e-4941-b16d-f3672384af61',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'LicenseManagement.jsx:loadLicenses:received',message:'Received licenses from API',data:{licenseCount:data.licenses?.length||0,firstLicenseExpiresAt:data.licenses?.[0]?.expires_at,hypothesisId:'A'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1'})}).catch(()=>{});
      // #endregion
      setLicenses(data.licenses || []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Lizenzen');
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // loadLicenses stabil via useCallback([filters]); bewusst nur diese Abhängigkeit
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    console.log('LicenseManagement useEffect triggered, loading licenses');
    loadLicenses();
  }, [loadLicenses]);

  // MasterKey/Quoten beim Mount laden, damit die Tabelle angezeigt wird
  useEffect(() => {
    loadQuotas();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadQuotas = async () => {
    try {
      const data = await getMasterKeyQuotas();
      setQuotas(data.quotas || []);
    } catch {
      setQuotas([]);
    }
  };

  const handleCreateMasterKey = async (e) => {
    e.preventDefault();
    const key = masterKeyInput.trim();
    if (!key) return;
    setMasterKeyError('');
    setMasterKeySubmitting(true);
    try {
      await createMasterKey(key);
      setMasterKeyInput('');
      loadQuotas();
      loadLicenses();
    } catch (err) {
      const msg = err.message || 'MasterKey ungültig oder Backend-Fehler';
      setMasterKeyError(msg);
    } finally {
      setMasterKeySubmitting(false);
    }
  };

  const handleCreateLicense = async (e) => {
    e.preventDefault();
    try {
      await createLicense(
        adminInfo?.id,
        newLicense.product_code,
        newLicense.license_type,
        newLicense.expires_at || null
      );
      setShowCreateModal(false);
      setNewLicense({ product_code: 'ZEITECE', license_type: 'TRIAL', expires_at: '' });
      loadLicenses();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen der Lizenz');
    }
  };

  const handleDeactivate = async (licenseKey) => {
    if (window.confirm(t('alerts.confirm.license_deactivate'))) {
      try {
        await deactivateLicense(licenseKey);
        loadLicenses();
      } catch (err) {
        setError(err.message || 'Fehler beim Deaktivieren');
      }
    }
  };

  const handleDelete = async (licenseKey) => {
    if (window.confirm(t('alerts.confirm.license_delete'))) {
      try {
        await deleteLicense(licenseKey);
        loadLicenses();
      } catch (err) {
        setError(err.message || 'Fehler beim Löschen');
      }
    }
  };

  const openRenewModal = (license) => {
    setRenewTargetLicense(license);
    setRenewForm({
      extend_days: calculateDefaultDurationDays(license),
      expires_at: toDateTimeLocalValue(license.expires_at),
      mode: 'days',
    });
    setError('');
    setShowRenewModal(true);
  };

  const openRenewFromHeader = () => {
    if (renewableLicenses.length === 0) return;
    openRenewModal(renewableLicenses[0]);
  };

  const closeRenewModal = () => {
    setShowRenewModal(false);
    setRenewTargetLicense(null);
  };

  const handleRenewSubmit = async (e) => {
    e.preventDefault();
    if (!renewTargetLicense) return;
    setError('');
    setRenewSubmitting(true);
    try {
      const payload =
        renewForm.mode === 'days'
          ? { extend_days: Number(renewForm.extend_days) }
          : { expires_at: new Date(renewForm.expires_at).toISOString() };
      await renewLicense(renewTargetLicense.license_key, payload);
      closeRenewModal();
      loadLicenses();
    } catch (err) {
      setError(err.message || 'Fehler beim Verlängern');
    } finally {
      setRenewSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    console.log('formatDate CALLED with:', dateStr, 'Type:', typeof dateStr);
    try {
      if (!dateStr) {
        console.log('formatDate: No date string, returning N/A');
        return 'N/A';
      }
      const date = new Date(dateStr);
      console.log('formatDate: Parsed date:', date, 'Valid:', !isNaN(date.getTime()));
      if (isNaN(date.getTime())) {
        console.log('formatDate: Invalid date, returning raw string:', dateStr);
        return dateStr; // Fallback: Zeige rohen String
      }
      // Explizite Formatierung mit padStart für führende Nullen
      // WICHTIG: Verwende UTC-Methoden, um Zeitzonen-Konvertierung zu vermeiden
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, '0');
      const minutes = String(date.getUTCMinutes()).padStart(2, '0');
      const seconds = String(date.getUTCSeconds()).padStart(2, '0');
      const formatted = `${day}.${month}.${year}, ${hours}:${minutes}:${seconds}`;
      console.log('formatDate: Formatted result:', formatted);
      return formatted;
    } catch (error) {
      console.error('formatDate ERROR:', error, 'dateStr:', dateStr);
      return dateStr || 'N/A';
    }
  };

  return (
    <div className="license-management">
      <div className="page-header">
        <h2>Lizenzverwaltung</h2>
        <div className="page-header-actions" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            Neue Lizenz erstellen
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <section className="master-key-section" style={{ marginBottom: '24px', padding: '16px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>MasterKey / Quoten (Lizenz-Begrenzung)</h3>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280' }}>
          MasterKey-String einfügen (mit Tool erzeugt). Ersetzt die Quote für das im Key angegebene Produkt.
        </p>
        <form onSubmit={handleCreateMasterKey} style={{ marginBottom: '16px' }}>
          <textarea
            value={masterKeyInput}
            onChange={(e) => setMasterKeyInput(e.target.value)}
            placeholder="MasterKey-String hier einfügen …"
            rows={3}
            style={{ width: '100%', maxWidth: '600px', fontFamily: 'monospace', fontSize: '12px', padding: '8px', marginBottom: '8px' }}
          />
          {masterKeyError && <div className="error-message" style={{ marginBottom: '8px' }}>{masterKeyError}</div>}
          <button type="submit" className="btn btn-primary" disabled={masterKeySubmitting || !masterKeyInput.trim()}>
            {masterKeySubmitting ? 'Wird eingefügt …' : 'MasterKey einfügen'}
          </button>
        </form>
        {quotas.length > 0 && (
          <table className="admin-table" style={{ fontSize: '13px' }}>
            <thead>
              <tr>
                <th>Produkt</th>
                <th>Aktuell</th>
                <th>Maximal</th>
                <th>Gültig bis</th>
              </tr>
            </thead>
            <tbody>
              {quotas.map((q) => (
                <tr key={q.id}>
                  <td>{q.product_code === '*' ? 'Alle' : q.product_code}</td>
                  <td>{q.current_count}</td>
                  <td>{q.max_count}</td>
                  <td>{q.valid_until ? new Date(q.valid_until).toLocaleDateString('de-DE') : '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <div className="filters">
        <select
          value={filters.product_code}
          onChange={(e) => setFilters({ ...filters, product_code: e.target.value })}
        >
          <option value="">Alle Produkte</option>
          <option value="ZEITECE">ZEITECE</option>
          <option value="ZEITAUF-MOBIL">ZEITAUF-MOBIL</option>
          <option value="ZEITAUF4">ZEITAUF4</option>
          <option value="ZEITART5">ZEITART5</option>
          <option value="OPUSPLANW2">OPUSPLANW2</option>
        </select>
        <select
          value={filters.license_type}
          onChange={(e) => setFilters({ ...filters, license_type: e.target.value })}
        >
          <option value="">Alle Typen</option>
          <option value="PERMANENT">Permanent</option>
          <option value="SUBSCRIPTION">Subscription</option>
          <option value="NFR">NFR</option>
          <option value="TRIAL">Trial</option>
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="">Alle Status</option>
          <option value="true">Aktiv</option>
          <option value="false">Inaktiv</option>
        </select>
      </div>

      {loading ? (
        <div className="loading">Lade Lizenzen...</div>
      ) : (
        <>
        {console.log('LicenseManagement RENDERING TABLE, licenses count:', licenses.length, 'licenses:', licenses)}
        <table className="admin-table">
          <thead>
            <tr>
              <th>Lizenz-Key</th>
              <th>Produktcode</th>
              <th>Typ</th>
              <th>Benutzer</th>
              <th>Aktiviert</th>
              <th>Ablaufdatum (Uhrzeit)</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((license) => {
              // #region agent log
              console.log('Rendering license:', license.id, 'expires_at:', license.expires_at);
              // #endregion
              return (
              <tr key={license.id}>
                <td>
                  <Link to={`/admin/licenses/${license.license_key}`}>
                    {license.license_key}
                  </Link>
                </td>
                <td>{license.product_code}</td>
                <td>{license.license_type}</td>
                <td>{license.user_email || '-'}</td>
                <td>{formatDate(license.activated_at)}</td>
                <td>
                  {/* #region agent log */}
                  {(() => {
                    console.log('DEBUG expires_at RAW:', license.expires_at, 'Type:', typeof license.expires_at);
                    const result = formatDate(license.expires_at);
                    console.log('DEBUG expires_at FORMATTED:', result);
                    return result;
                  })()}
                  {/* #endregion */}
                </td>
                <td>
                  <span className={`badge ${license.is_active ? 'badge-success' : 'badge-danger'}`}>
                    {license.is_active ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <Link to={`/admin/licenses/${license.license_key}`} className="btn btn-secondary">
                      Details
                    </Link>
                    {license.is_active && (
                      <button
                        className="btn btn-warning"
                        onClick={() => handleDeactivate(license.license_key)}
                      >
                        Deaktivieren
                      </button>
                    )}
                    <button
                      className="btn btn-danger"
                      onClick={() => handleDelete(license.license_key)}
                    >
                      Löschen
                    </button>
                  </div>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
        </>
      )}

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Neue Lizenz erstellen</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateLicense}>
              <div className="form-group">
                <label>Produktcode</label>
                <select
                  value={newLicense.product_code}
                  onChange={(e) => setNewLicense({ ...newLicense, product_code: e.target.value })}
                  required
                >
                  <option value="ZEITECE">ZEITECE</option>
                  <option value="ZEITAUF-MOBIL">ZEITAUF-MOBIL</option>
                  <option value="ZEITAUF4">ZEITAUF4</option>
                  <option value="ZEITART5">ZEITART5</option>
                  <option value="OPUSPLANW2">OPUSPLANW2</option>
                </select>
              </div>
              <div className="form-group">
                <label>Lizenz-Typ</label>
                <select
                  value={newLicense.license_type}
                  onChange={(e) => setNewLicense({ ...newLicense, license_type: e.target.value })}
                  required
                >
                  <option value="PERMANENT">Permanent</option>
                  <option value="SUBSCRIPTION">Subscription</option>
                  <option value="NFR">NFR</option>
                  <option value="TRIAL">Trial (30 Tage)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ablaufdatum (optional)</label>
                <input
                  type="datetime-local"
                  value={newLicense.expires_at}
                  onChange={(e) => setNewLicense({ ...newLicense, expires_at: e.target.value })}
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn btn-primary">
                  Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default LicenseManagement;






